import {useRef, useState} from 'react';
import {Box, Flex, Image, Text} from '@chakra-ui/react';
import {DriveFile} from '../drive/types';
import {formatDisplayDate, parseEventFolderName, parseFolderName} from '../domain/naming';
import {useAlbumCover, useEventInfo} from '../drive/queries';
import {useNearViewport} from '../hooks/useNearViewport';
import {useLongPress} from '../hooks/useLongPress';
import {NoexportBadge} from './NoexportBadge';

interface FolderCardProps {
    folder: DriveFile;
    driveId: string;
    /** The folder's info.json, if it has one (from the drive-wide index). */
    infoFile: DriveFile | undefined;
    selected: boolean;
    onSelect: (e: React.MouseEvent) => void;
    onOpen: () => void;
    onContextMenu?: (e: React.MouseEvent) => void;
}

function FolderGlyph({size = 40}: { size?: number }) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
            <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
        </svg>
    );
}

/**
 * A subfolder card — shown with a date/title treatment when its name matches the event-folder convention, plain otherwise.
 * An album with a cover shows that picture, the folder glyph moving next to its name.
 */
export function FolderCard({folder, driveId, infoFile, selected, onSelect, onOpen, onContextMenu}: FolderCardProps) {
    // A noexport folder is shown without its prefix, so an event folder's date/title still applies.
    const {displayName, noexport} = parseFolderName(folder.name);
    const parsed = parseEventFolderName(displayName);
    const containerRef = useRef<HTMLDivElement>(null);
    const longPress = useLongPress(onContextMenu);
    const isNearViewport = useNearViewport(containerRef);
    // Downloaded once per info.json version (then kept across reloads), and only for cards about to be seen.
    const infoQuery = useEventInfo(infoFile, {enabled: isNearViewport});
    const title = parsed ? (infoQuery.data?.title || parsed.slug) : displayName;
    const subtitle = parsed ? formatDisplayDate(parsed.date) : undefined;
    const coverUrl = useAlbumCover(driveId, folder.id, infoQuery.data?.cover, isNearViewport).data?.thumbnailLink;
    // By URL, so a changed cover starts over; a thumbnail that fails to load leaves the plain folder.
    const [loadedUrl, setLoadedUrl] = useState<string>();
    const [failedUrl, setFailedUrl] = useState<string>();
    const showsCover = !!coverUrl && loadedUrl === coverUrl;

    return (
        <Box
            ref={containerRef}
            position="relative"
            borderWidth="2px"
            borderRadius="lg"
            overflow="hidden"
            cursor="pointer"
            borderColor={selected ? 'blue.400' : 'gray.200'}
            onClick={onSelect}
            onDoubleClick={onOpen}
            {...longPress}
            // No iOS callout (image/link menu) on a long press: it opens our menu instead.
            css={{WebkitTouchCallout: 'none'}}
        >
            <Box
                position="relative"
                bg="blue.50"
                color="blue.400"
                aspectRatio={3 / 2}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                {coverUrl && failedUrl !== coverUrl &&
                    <Image
                        src={coverUrl}
                        alt=""
                        position="absolute"
                        inset={0}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                        // Hidden until loaded, so the folder glyph stays in the middle meanwhile.
                        opacity={showsCover ? 1 : 0}
                        onLoad={() => setLoadedUrl(coverUrl)}
                        onError={() => setFailedUrl(coverUrl)}
                        referrerPolicy="no-referrer"
                    />
                }
                {!showsCover && <FolderGlyph/>}
                {noexport && <NoexportBadge position="absolute" top={2} left={2}/>}
            </Box>
            <Flex p="0 10px 8px 10px" align="center" gap={2}>
                {showsCover && <Box flexShrink={0} mt="1" color="blue.400"><FolderGlyph size={32}/></Box>}
                <Box minW={0}>
                    <Text mt="1" fontWeight="semibold" as="h5" lineHeight="tight" lineClamp={1}>
                        {title}
                    </Text>
                    {subtitle && <Text color="gray.500" fontSize="sm">{subtitle}</Text>}
                </Box>
            </Flex>
        </Box>
    );
}
